import { useState, FormEvent, CSSProperties, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { getDeviceId } from '../device';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { useLanguage } from '../context/LanguageContext';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';

const STEP_COUNT = 3;
// Instagram-style username: 3–20 chars, letters/numbers/period/underscore,
// must start and end alphanumeric (no leading/trailing dot or underscore).
const USERNAME_RE = /^[a-z0-9][a-z0-9._]{1,18}[a-z0-9]$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { login } = useAuth();
  const { colors } = useTheme();
  const toast = useToast();
  const { language } = useLanguage();
  // Prefill the referral code when arriving from a share link (/register?ref=CODE).
  const referralFromLink = (searchParams.get('ref') || '').trim().toUpperCase();
  const [form, setForm] = useState({
    fullName: '', username: '', password: '', confirmPassword: '',
    email: '', phone: '+1 ', referralCode: referralFromLink,
  });
  const [step, setStep] = useState(0);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyContent, setPolicyContent] = useState('');
  const [policyLoading, setPolicyLoading] = useState(false);

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));
  // Strip anything that isn't a valid username char as the user types, so
  // spaces and stray symbols can never make it into the field.
  const setUsername = (value: string) => set('username', value.toLowerCase().replace(/[^a-z0-9._]/g, ''));

  const openPolicy = async () => {
    setShowPolicy(true);
    if (policyContent) return;
    setPolicyLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/privacy-policy`);
      setPolicyContent(res.data.content || t('auth.register.policyEmpty'));
    } catch {
      setPolicyContent(t('auth.register.policyFailed'));
    } finally {
      setPolicyLoading(false);
    }
  };

  const fail = (titleKey: string, msgKey: string) => { toast.error(t(titleKey), t(msgKey)); return false; };

  // Validate just the fields that live on a given step, surfacing the first
  // problem as a toast. Returns true when the step is good to advance/submit.
  const validateStep = (s: number): boolean => {
    if (s === 0) {
      if (!USERNAME_RE.test(form.username)) return fail('auth.register.validation.usernameInvalidTitle', 'auth.register.validation.usernameInvalid');
      if (form.password.length < 8) return fail('auth.register.validation.weakPasswordTitle', 'auth.register.validation.weakPassword');
      if (form.password !== form.confirmPassword) return fail('auth.register.validation.mismatchTitle', 'auth.register.validation.mismatch');
    }
    if (s === 1) {
      if (!form.fullName.trim()) return fail('auth.register.validation.missingFieldTitle', 'auth.register.validation.fullNameRequired');
      if (!form.email.trim()) return fail('auth.register.validation.missingFieldTitle', 'auth.register.validation.emailRequired');
      if (!EMAIL_RE.test(form.email.trim())) return fail('auth.register.validation.invalidEmailTitle', 'auth.register.validation.invalidEmail');
      if (!form.phone.trim()) return fail('auth.register.validation.missingFieldTitle', 'auth.register.validation.phoneRequired');
    }
    return true;
  };

  const handleNext = () => { if (validateStep(step)) setStep(s => Math.min(s + 1, STEP_COUNT - 1)); };
  const handleBack = () => { if (step === 0) navigate('/login'); else setStep(s => s - 1); };

  // Enter / button press: advance through steps, submit on the last one.
  const onSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (step < STEP_COUNT - 1) handleNext();
    else handleRegister();
  };

  const handleRegister = async () => {
    // Re-check earlier steps defensively and jump back to the first offender.
    if (!validateStep(0)) { setStep(0); return; }
    if (!validateStep(1)) { setStep(1); return; }
    if (!agreedToTerms) { fail('auth.register.validation.termsRequiredTitle', 'auth.register.validation.termsRequired'); return; }

    setLoading(true);
    try {
      const deviceId = await getDeviceId().catch(() => null);
      // H1 phase 2 — withCredentials so the refresh-token cookie lands.
      const res = await axios.post(
        `${API_BASE}/auth/register`,
        {
          fullName: form.fullName.trim(),
          username: form.username.trim().toLowerCase(),
          password: form.password,
          email: form.email.trim(),
          phone: form.phone.trim(),
          referralCode: form.referralCode.trim().toUpperCase() || undefined,
          preferredLanguage: language,
        },
        {
          withCredentials: true,
          headers: deviceId ? { 'X-Device-Id': deviceId } : undefined,
        },
      );
      const { access_token, user } = res.data;
      await login(access_token, user.id);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number } };
      const status = e.response?.status;
      const serverMsg = e.response?.data?.message;
      if (status === 409) {
        const usernameTaken = !serverMsg?.toLowerCase().startsWith('that email') && !serverMsg?.toLowerCase().startsWith('that phone');
        const title = serverMsg?.toLowerCase().startsWith('that email')
          ? t('auth.register.errors.emailUsedTitle')
          : serverMsg?.toLowerCase().startsWith('that phone')
            ? t('auth.register.errors.phoneUsedTitle')
            : t('auth.register.errors.usernameTakenTitle');
        // Send them back to the step that owns the conflicting field.
        setStep(usernameTaken ? 0 : 1);
        toast.error(title, serverMsg || t('auth.register.errors.usernameInUse'));
      }
      else if (status === 403) toast.error(t('auth.register.errors.deviceLimitTitle'), serverMsg || t('auth.register.errors.deviceLimit'));
      else toast.error(t('auth.register.errors.failedTitle'), serverMsg || t('auth.register.errors.failed'));
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = [
    t('auth.register.steps.account'),
    t('auth.register.steps.aboutYou'),
    t('auth.register.steps.finish'),
  ];
  const isLast = step === STEP_COUNT - 1;

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg, padding: '24px max(24px, env(safe-area-inset-left))' }}>
      <div style={{ width: '100%', maxWidth: 460, margin: '0 auto' }}>
        <button
          onClick={handleBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: colors.textMuted, fontSize: 14, marginBottom: 20, minHeight: 44,
          }}
        >
          <Icon name="arrow-left" size={16} color={colors.textMuted} />
          {step === 0 ? t('auth.register.backToSignIn') : t('auth.register.back')}
        </button>

        {/* Progress: three segments fill as the user advances. */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <div key={i} style={{
                flex: 1, height: 4, borderRadius: 999,
                backgroundColor: i <= step ? colors.accent : colors.cardBorder,
                transition: 'background-color 0.2s',
              }} />
            ))}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, letterSpacing: 0.3, textTransform: 'uppercase' }}>
            {t('auth.register.stepOf', { current: step + 1, total: STEP_COUNT })}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: colors.text, marginTop: 2 }}>{stepTitles[step]}</div>
        </div>

        <form
          onSubmit={onSubmit}
          style={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: 16,
            padding: 22,
          }}
        >
          {step === 0 && (
            <>
              <Field label={t('auth.register.fields.username')}>
                <Icon name="at" size={15} color={colors.textSub} style={iconPad} />
                <input style={inputStyle(colors.inputText)} placeholder={t('auth.register.placeholders.username')}
                  value={form.username} onChange={e => setUsername(e.target.value)} maxLength={20} autoCapitalize="none" autoCorrect="off" autoFocus />
              </Field>
              <FieldHint>{t('auth.register.hints.username')}</FieldHint>

              <Field label={t('auth.register.fields.password')}>
                <Icon name="lock" size={15} color={colors.textSub} style={iconPad} />
                <input style={{ ...inputStyle(colors.inputText), flex: 1 }} placeholder={t('auth.register.placeholders.password')}
                  type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={e => set('password', e.target.value)} autoCapitalize="none" autoComplete="new-password" />
                <button type="button" aria-label={showPass ? t('auth.register.hidePassword') : t('auth.register.showPassword')} onClick={() => setShowPass(v => !v)}
                  style={eyeBtnStyle}>
                  <Icon name={showPass ? 'eye-slash' : 'eye'} size={15} color={colors.textSub} />
                </button>
              </Field>
              <Field label={t('auth.register.fields.confirmPassword')}>
                <Icon name="lock" size={15} color={colors.textSub} style={iconPad} />
                <input style={inputStyle(colors.inputText)} placeholder={t('auth.register.placeholders.confirmPassword')}
                  type={showPass ? 'text' : 'password'} value={form.confirmPassword}
                  onChange={e => set('confirmPassword', e.target.value)} autoCapitalize="none" autoComplete="new-password" />
              </Field>
            </>
          )}

          {step === 1 && (
            <>
              <Field label={t('auth.register.fields.fullName')}>
                <Icon name="user" size={15} color={colors.textSub} style={iconPad} />
                <input style={inputStyle(colors.inputText)} placeholder={t('auth.register.placeholders.fullName')}
                  value={form.fullName} onChange={e => set('fullName', e.target.value)} autoCapitalize="words" autoFocus />
              </Field>
              <Field label={t('auth.register.fields.email')}>
                <Icon name="envelope" size={15} color={colors.textSub} style={iconPad} />
                <input style={inputStyle(colors.inputText)} placeholder={t('auth.register.placeholders.email')}
                  type="email" value={form.email} onChange={e => set('email', e.target.value.trim())} autoCapitalize="none" autoComplete="email" />
              </Field>
              <Field label={t('auth.register.fields.phone')}>
                <Icon name="phone" size={15} color={colors.textSub} style={iconPad} />
                <input style={inputStyle(colors.inputText)} placeholder={t('auth.register.placeholders.phone')}
                  type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} autoComplete="tel" />
              </Field>
            </>
          )}

          {step === 2 && (
            <>
              <Field label={t('auth.register.fields.referral')}>
                <Icon name="gift" size={15} color={colors.textSub} style={iconPad} />
                <input style={inputStyle(colors.inputText)} placeholder={t('auth.register.placeholders.referral')}
                  maxLength={16} value={form.referralCode}
                  onChange={e => set('referralCode', e.target.value.toUpperCase())} autoCapitalize="characters" autoCorrect="off" />
              </Field>

              <div
                onClick={() => setAgreedToTerms(v => !v)}
                role="checkbox"
                aria-checked={agreedToTerms}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginTop: 18,
                  minHeight: 44, cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 20, height: 20, borderRadius: 5,
                    border: `2px solid ${agreedToTerms ? '#14b8a6' : '#475569'}`,
                    backgroundColor: agreedToTerms ? '#14b8a6' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {agreedToTerms && <Icon name="check" size={11} color="#fff" />}
                </div>
                <div style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.5, flex: 1 }}>
                  {t('auth.register.terms.accept')}
                  <span
                    onClick={e => { e.stopPropagation(); openPolicy(); }}
                    style={{ color: '#60a5fa', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    {t('auth.register.terms.link')}
                  </span>
                </div>
              </div>
            </>
          )}

          <Button
            label={isLast ? t('auth.register.submit') : t('auth.register.continue')}
            icon={isLast ? 'user-plus' : 'arrow-right'}
            loading={isLast && loading}
            type="submit"
            style={{ marginTop: 24 }}
          />
        </form>
      </div>

      <Modal open={showPolicy} onClose={() => setShowPolicy(false)} contentStyle={{ maxHeight: '80%' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{t('auth.register.privacyTitle')}</div>
        <div style={{ maxHeight: 400, overflowY: 'auto', fontSize: 14, lineHeight: 1.55, color: colors.textSub, whiteSpace: 'pre-wrap' }}>
          {policyLoading ? <div style={{ textAlign: 'center', padding: 20 }}>{t('actions.loading')}</div> : policyContent}
        </div>
        <Button label={t('actions.close')} onClick={() => setShowPolicy(false)} style={{ marginTop: 16 }} />
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  const { colors } = useTheme();
  return (
    <div>
      <div
        style={{
          fontSize: 12, fontWeight: 700, color: colors.textMuted,
          marginBottom: 6, marginTop: 14, letterSpacing: 0.3, textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex', alignItems: 'center',
          backgroundColor: colors.input,
          border: `1px solid ${colors.inputBorder}`,
          borderRadius: 8, minHeight: 50,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function FieldHint({ children }: { children: ReactNode }) {
  const { colors } = useTheme();
  return <div style={{ fontSize: 12, color: colors.textSub, marginTop: 6, lineHeight: 1.4 }}>{children}</div>;
}

const iconPad: CSSProperties = { paddingLeft: 14, paddingRight: 10 };
const eyeBtnStyle: CSSProperties = {
  width: 50, minHeight: 50, background: 'transparent', border: 'none',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const inputStyle = (color: string): CSSProperties => ({
  flex: 1, padding: '13px 14px 13px 0',
  fontSize: 15, color, background: 'transparent', border: 'none', outline: 'none', width: '100%',
});
